import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

// ── Color constants (KeywordIP palette) ──────────────────────────────────────
const C = {
  critical: '#dc2626',
  warning:  '#d97706',
  info:     '#2563eb',
  good:     '#10b981',
  technical:   '#7c3aed',
  content:     '#0891b2',
  performance: '#ea580c',
};

const scoreColor = (s) => s >= 80 ? C.good : s >= 50 ? C.warning : C.critical;
const scoreLabel = (s) => s >= 80 ? 'Good' : s >= 50 ? 'Needs Work' : 'Poor';

// ── Health score gauge (SVG, no extra deps) ──────────────────────────────────
function HealthGauge({ score }) {
  const color = scoreColor(score);
  const label = scoreLabel(score);
  const r = 60;
  const cx = 80, cy = 80;
  const circumference = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference * 0.75;
  const max = circumference * 0.75;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ position: 'relative', width: 160, height: 160 }}>
        <svg width="160" height="160" style={{ transform: 'rotate(135deg)' }}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth="14"
            strokeDasharray={`${max} ${circumference}`} strokeLinecap="round" />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="14"
            strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.6s ease-out' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{Math.round(score)}</span>
          <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>/100</span>
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color, marginTop: 6 }}>{label}</div>
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color = '#64748b', sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color, marginTop: 1, fontWeight: 600 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Single issue row (collapsible) ───────────────────────────────────────────
function IssueRow({ issue }) {
  const [open, setOpen] = useState(false);
  const sevColor = C[issue.severity] || '#64748b';
  const catColor = C[issue.category] || '#64748b';

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8, overflow: 'hidden', background: '#fff' }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '3px 8px', borderRadius: 4, background: sevColor + '15', color: sevColor }}>
          {issue.severity}
        </span>
        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: `1px solid ${catColor}33`, color: catColor, textTransform: 'capitalize' }}>
          {issue.category}
        </span>
        <span style={{ flex: 1, fontSize: 14, color: '#0f172a', fontWeight: 500 }}>{issue.title}</span>
        {issue.page_url && (
          <a href={issue.page_url} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ fontSize: 11, color: '#3b82f6', textDecoration: 'none' }}>
            ↗
          </a>
        )}
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '12px 14px', borderTop: '1px solid #f1f5f9', background: '#fafbfc', fontSize: 13 }}>
          {issue.page_url && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>URL: </span>
              <span style={{ color: '#3b82f6', wordBreak: 'break-all' }}>{issue.page_url}</span>
            </div>
          )}
          {issue.affected_value && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12 }}>
              <span style={{ color: '#94a3b8' }}>Value: </span>
              <span style={{ color: '#9a3412', fontFamily: 'monospace' }}>{issue.affected_value}</span>
            </div>
          )}
          <div style={{ color: '#475569', marginBottom: 8 }}>{issue.description}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: '#047857', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '8px 10px', borderRadius: 6 }}>
            <span style={{ marginTop: 1 }}>✓</span>
            <span>{issue.recommendation}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Issues panel with severity tabs + category filter ────────────────────────
function IssuesPanel({ issuesBySeverity }) {
  const [activeTab, setActiveTab] = useState('critical');
  const [catFilter, setCatFilter] = useState('all');

  const tabs = [
    { key: 'critical', label: 'Critical', color: C.critical },
    { key: 'warning',  label: 'Warnings', color: C.warning },
    { key: 'info',     label: 'Info', color: C.info },
  ];

  // If active tab is empty, jump to first tab that has issues
  useEffect(() => {
    if ((issuesBySeverity[activeTab] || []).length === 0) {
      const next = tabs.find(t => (issuesBySeverity[t.key] || []).length > 0);
      if (next) setActiveTab(next.key);
    }
  }, [issuesBySeverity]); // eslint-disable-line

  const issues = issuesBySeverity[activeTab] || [];
  const filtered = catFilter === 'all' ? issues : issues.filter(i => i.category === catFilter);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${activeTab === tab.key ? tab.color : '#e5e7eb'}`,
              background: activeTab === tab.key ? tab.color + '15' : '#fff',
              color: activeTab === tab.key ? tab.color : '#64748b',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
            {tab.label} <span style={{ opacity: 0.7 }}>({(issuesBySeverity[tab.key] || []).length})</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['all', 'technical', 'content', 'performance'].map(cat => (
          <button key={cat} onClick={() => setCatFilter(cat)}
            style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
              border: `1px solid ${catFilter === cat ? (C[cat] || '#94a3b8') : '#e5e7eb'}`,
              background: catFilter === cat ? (C[cat] || '#94a3b8') + '15' : '#fff',
              color: catFilter === cat ? (C[cat] || '#475569') : '#94a3b8',
              cursor: 'pointer',
            }}>
            {cat}
          </button>
        ))}
      </div>

      <div style={{ maxHeight: 600, overflowY: 'auto', paddingRight: 4 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
            No issues in this category.
          </div>
        ) : (
          filtered.map((issue, i) => <IssueRow key={i} issue={issue} />)
        )}
      </div>
    </div>
  );
}

// ── Pages table (paginated) ───────────────────────────────────────────────────
function PagesTable({ auditId }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 25;

  useEffect(() => {
    setLoading(true);
    api.getSiteAuditPages(auditId, page, PER_PAGE).then(data => {
      setPages(data.pages || []);
      setTotal(data.total || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [auditId, page]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>Loading pages…</div>;

  return (
    <div>
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', color: '#64748b' }}>
              <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>URL</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Issues</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Words</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Size</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Load</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Indexed</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Schema</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p, i) => {
              const statusColor = p.status_code >= 400 ? C.critical : p.status_code >= 300 ? C.warning : C.good;
              const loadColor = p.load_time_ms > 3000 ? C.critical : p.load_time_ms > 1500 ? C.warning : C.good;
              return (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 14px', maxWidth: 320 }}>
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#3b82f6', textDecoration: 'none', fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.url.replace(/^https?:\/\//, '')}
                    </a>
                    {p.title && <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>{p.title}</div>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: statusColor }}>{p.status_code || '—'}</span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    {p.issues_count > 0 ? (
                      <span style={{ background: C.critical + '15', color: C.critical, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>{p.issues_count}</span>
                    ) : (
                      <span style={{ color: C.good }}>✓</span>
                    )}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: 12, color: '#475569' }}>{p.word_count?.toLocaleString() || 0}</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: 12, color: '#475569' }}>{p.page_size_kb}KB</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: 12, color: loadColor, fontWeight: 600 }}>{Math.round(p.load_time_ms)}ms</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: 13 }}>
                    {p.is_indexable ? <span style={{ color: C.good }}>✓</span> : <span style={{ color: C.critical }}>✗</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: 13 }}>
                    {p.has_schema ? <span style={{ color: C.good }}>✓</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 12, color: '#64748b' }}>
        <span>{total} pages total</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: 12 }}>
            Prev
          </button>
          <span style={{ padding: '6px 12px' }}>Page {page} of {Math.ceil(total / PER_PAGE) || 1}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={pages.length < PER_PAGE}
            style={{ padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: pages.length < PER_PAGE ? 'not-allowed' : 'pointer', opacity: pages.length < PER_PAGE ? 0.4 : 1, fontSize: 12 }}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Site Audit component ─────────────────────────────────────────────────
function SiteAudit({ websiteId, websiteDomain }) {
  const [audits, setAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [results, setResults] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [starting, setStarting] = useState(false);
  const pollIntervalRef = useRef(null);

  const loadResults = useCallback((auditId) => {
    setLoading(true);
    api.getSiteAuditResults(auditId).then(data => {
      setResults(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const startPolling = useCallback((auditId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setPolling(true);
    pollIntervalRef.current = setInterval(() => {
      api.getSiteAuditStatus(auditId).then(data => {
        setSelectedAudit(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPolling(false);
          loadAudits();
          if (data.status === 'completed') loadResults(auditId);
        }
      }).catch(() => {});
    }, 3000);
  }, [loadResults]); // eslint-disable-line

  const loadAudits = useCallback(() => {
    if (!websiteId) return;
    api.listSiteAudits(websiteId).then(data => {
      setAudits(data || []);
      // Auto-select latest if none selected
      if ((data || []).length > 0 && !selectedAudit) {
        const latest = data[0];
        setSelectedAudit(latest);
        if (latest.status === 'completed') loadResults(latest.id);
        else if (latest.status === 'running') startPolling(latest.id);
      }
    });
  }, [websiteId, selectedAudit, loadResults, startPolling]);

  useEffect(() => { loadAudits(); }, [websiteId]); // eslint-disable-line
  useEffect(() => () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); }, []);

  const startAudit = () => {
    setStarting(true);
    setResults(null);
    api.startSiteAudit(websiteId, 500).then(data => {
      const newAudit = { id: data.audit_id, status: 'running', pages_crawled: 0 };
      setSelectedAudit(newAudit);
      setStarting(false);
      loadAudits();
      startPolling(data.audit_id);
    }).catch(err => {
      alert(err.response?.data?.error || 'Failed to start audit');
      setStarting(false);
    });
  };

  const exportReport = (type) => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    const url = `/keyword-intelligence/api/v1/site-audit/export/${selectedAudit.id}?type=${type}`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `site-audit-${selectedAudit.id}-${type}.csv`;
        link.click();
      });
  };

  // Empty state
  if (!websiteId) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🌐</div>
        <h3>No Website Selected</h3>
        <p>Please select a website to run a site audit.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 className="page-title" style={{ marginBottom: 4 }}>🛡️ Site Audit</h2>
          <div style={{ fontSize: 13, color: '#64748b' }}>{websiteDomain || ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {results && (
            <>
              <button onClick={() => exportReport('issues')}
                style={{ padding: '8px 14px', fontSize: 13, fontWeight: 500, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#475569', cursor: 'pointer' }}>
                ⬇ Export Issues
              </button>
              <button onClick={() => exportReport('pages')}
                style={{ padding: '8px 14px', fontSize: 13, fontWeight: 500, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#475569', cursor: 'pointer' }}>
                ⬇ Export Pages
              </button>
            </>
          )}
          <button onClick={startAudit} disabled={starting || polling}
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: 600,
              background: (starting || polling) ? '#94a3b8' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', border: 'none', borderRadius: 8,
              cursor: (starting || polling) ? 'not-allowed' : 'pointer',
            }}>
            {polling ? `⏳ Crawling… ${selectedAudit?.pages_crawled || 0} pages`
             : starting ? '⏳ Starting…'
             : '▶ Run New Audit'}
          </button>
        </div>
      </div>

      {/* Audit history pills */}
      {audits.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
          {audits.map(audit => (
            <button key={audit.id}
              onClick={() => { setSelectedAudit(audit); if (audit.status === 'completed') loadResults(audit.id); }}
              style={{
                flexShrink: 0, padding: '6px 12px', fontSize: 12, borderRadius: 8,
                border: `1px solid ${selectedAudit?.id === audit.id ? '#6366f1' : '#e5e7eb'}`,
                background: selectedAudit?.id === audit.id ? '#eef2ff' : '#fff',
                color: selectedAudit?.id === audit.id ? '#4338ca' : '#64748b',
                cursor: 'pointer', fontWeight: 500,
              }}>
              {new Date(audit.started_at).toLocaleDateString()} · Score {Math.round(audit.health_score)}
            </button>
          ))}
        </div>
      )}

      {/* Crawling banner */}
      {polling && selectedAudit && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 20 }}>⏳</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#1e40af', fontSize: 14 }}>Crawling in progress</div>
            <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>
              {selectedAudit.pages_crawled || 0} pages crawled. This usually takes 1–5 minutes.
            </div>
          </div>
          <div style={{ width: 200, height: 6, background: '#dbeafe', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, ((selectedAudit.pages_crawled || 0) / 500) * 100)}%`, background: '#3b82f6', transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {/* No audits yet */}
      {!polling && !loading && !results && audits.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛡️</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>No audits yet</h3>
          <p style={{ fontSize: 13, color: '#64748b', maxWidth: 400, margin: '0 auto 20px' }}>
            Run your first site audit to discover technical, content, and performance issues across up to 500 pages.
          </p>
          <button onClick={startAudit} disabled={starting}
            style={{ padding: '12px 24px', fontSize: 14, fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            ▶ Start First Audit
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 12 }}>Loading audit results…</div>
        </div>
      )}

      {/* Results */}
      {results && !loading && (
        <>
          {/* Score + stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 14, marginBottom: 16 }}>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 200 }}>
              <HealthGauge score={results.audit.health_score} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Health Score</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
              <StatCard icon="📄" label="Pages crawled" value={results.summary.total_pages} color="#3b82f6" />
              <StatCard icon="⚠" label="Critical issues" value={results.audit.critical_issues}
                color={C.critical}
                sub={results.audit.critical_issues > 0 ? 'Fix immediately' : 'None found'} />
              <StatCard icon="⚡" label="Avg load time" value={`${Math.round(results.summary.avg_load_time_ms)}ms`}
                color={results.summary.avg_load_time_ms > 3000 ? C.critical : results.summary.avg_load_time_ms > 1500 ? C.warning : C.good} />
              <StatCard icon="🔍" label="Indexable" value={results.summary.indexable_pages}
                color={C.good} sub={`of ${results.summary.total_pages} total`} />
            </div>
          </div>

          {/* Issues breakdown row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 16 }}>
            {/* By severity */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>By Severity</div>
              {[
                { label: 'Critical', value: results.audit.critical_issues, color: C.critical },
                { label: 'Warning', value: results.audit.warning_issues, color: C.warning },
                { label: 'Info', value: results.audit.info_issues, color: C.info },
              ].map(row => {
                const total = results.audit.critical_issues + results.audit.warning_issues + results.audit.info_issues || 1;
                const pct = (row.value / total) * 100;
                return (
                  <div key={row.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#475569' }}>{row.label}</span>
                      <span style={{ color: row.color, fontWeight: 700 }}>{row.value}</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: row.color, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* By category */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>By Category</div>
              {['technical', 'content', 'performance'].map(cat => {
                const count = (results.issues_by_category[cat] || []).length;
                const total = ['technical', 'content', 'performance'].reduce((s, c) => s + (results.issues_by_category[c] || []).length, 0) || 1;
                const pct = (count / total) * 100;
                return (
                  <div key={cat} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#475569', textTransform: 'capitalize' }}>{cat}</span>
                      <span style={{ color: C[cat], fontWeight: 700 }}>{count}</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: C[cat], transition: 'width 0.4s' }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Speed distribution */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>Page Speed</div>
              {[
                { label: 'Fast (<1s)', value: results.summary.load_time_distribution.fast, color: C.good },
                { label: 'Moderate', value: results.summary.load_time_distribution.moderate, color: C.warning },
                { label: 'Slow (>3s)', value: results.summary.load_time_distribution.slow, color: C.critical },
              ].map(row => {
                const total = results.summary.total_pages || 1;
                const pct = (row.value / total) * 100;
                return (
                  <div key={row.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#475569' }}>{row.label}</span>
                      <span style={{ color: row.color, fontWeight: 700 }}>{row.value}</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: row.color, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top issue types */}
          {results.top_issue_types && results.top_issue_types.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>Most Common Issues</div>
              {results.top_issue_types.slice(0, 8).map((row, i) => {
                const max = results.top_issue_types[0]?.count || 1;
                const pct = (row.count / max) * 100;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <span style={{ flex: '0 0 200px', fontSize: 12, color: '#475569', textTransform: 'capitalize' }}>
                      {row.issue_type.replace(/_/g, ' ')}
                    </span>
                    <div style={{ flex: 1, height: 14, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: '#6366f1', transition: 'width 0.4s' }} />
                    </div>
                    <span style={{ flex: '0 0 32px', fontSize: 12, color: '#0f172a', fontWeight: 700, textAlign: 'right' }}>{row.count}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* View tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 14, background: '#f1f5f9', padding: 4, borderRadius: 10, width: 'fit-content' }}>
            {['overview', 'issues', 'pages'].map(view => (
              <button key={view} onClick={() => setActiveView(view)}
                style={{
                  padding: '8px 18px', borderRadius: 6, border: 'none',
                  background: activeView === view ? '#fff' : 'transparent',
                  color: activeView === view ? '#0f172a' : '#64748b',
                  fontSize: 13, fontWeight: 600, textTransform: 'capitalize', cursor: 'pointer',
                  boxShadow: activeView === view ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}>
                {view}
              </button>
            ))}
          </div>

          {/* Active view content */}
          {activeView === 'overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.critical, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⚠ Critical Issues to Fix First
                </div>
                {(results.issues_by_severity.critical || []).slice(0, 5).map((issue, i) => (
                  <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{issue.title}</div>
                    {issue.page_url && (
                      <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {issue.page_url}
                      </div>
                    )}
                  </div>
                ))}
                {(results.issues_by_severity.critical || []).length === 0 && (
                  <div style={{ color: C.good, fontSize: 13, fontWeight: 500 }}>✓ No critical issues found</div>
                )}
              </div>

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ✓ Site Health Summary
                </div>
                {[
                  { label: 'Pages Indexed', value: `${results.summary.indexable_pages} / ${results.summary.total_pages}`, good: results.summary.indexable_pages === results.summary.total_pages },
                  { label: 'Pages with Schema', value: `${results.summary.pages_with_schema} / ${results.summary.total_pages}`, good: results.summary.pages_with_schema > 0 },
                  { label: 'Avg Word Count', value: Math.round(results.summary.avg_word_count), good: results.summary.avg_word_count > 400 },
                  { label: 'Avg Load Time', value: `${Math.round(results.summary.avg_load_time_ms)}ms`, good: results.summary.avg_load_time_ms < 2000 },
                  { label: 'Fast Pages', value: results.summary.load_time_distribution.fast, good: true },
                  { label: 'Slow Pages (>3s)', value: results.summary.load_time_distribution.slow, good: results.summary.load_time_distribution.slow === 0 },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{row.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: row.good ? C.good : C.warning }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeView === 'issues' && (
            <IssuesPanel issuesBySeverity={results.issues_by_severity} />
          )}

          {activeView === 'pages' && (
            <PagesTable auditId={selectedAudit.id} />
          )}
        </>
      )}
    </div>
  );
}

export default SiteAudit;
